import { Container, type ContainerWidth } from '../Container';

import type { ReactElement, ReactNode } from 'react';

/**
 * Page-top masthead for inner marketing pages: an eyebrow + `<h1>` +
 * description + optional CTA slot.
 *
 * It carries exactly two brand devices, both decorative and both cheap:
 *
 * 1. **A brand-gradient rule along the bottom edge**, replacing the flat
 *    hairline. It is the same accent rule `Panel accent` draws across the top of
 *    a result card, and it is what makes ~25 otherwise-identical white
 *    mastheads read as belonging to the landing page rather than as a default
 *    `<h1>` on a blank document.
 * 2. **A very faint brand wash** behind the copy — a radial at ~5% alpha, so
 *    the ground stays within a hair of `--bd-page` and every text token keeps
 *    its measured contrast. (`--bd-tint`, the much stronger selection ground,
 *    is the value the contrast suite gates; this is lighter than that.)
 *
 * The root is a `<header>` element scoped inside the page's `<main>` (a generic
 * content header, not the site banner), so it needs no aria-label: the `<h1>`
 * names it. Both layers are `aria-hidden` and never enter the accessibility
 * tree.
 */
export interface PageHeroProps {
  readonly eyebrow?: ReactNode;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  /** id for the `<h1>` (optional aria-labelledby wiring). */
  readonly titleId?: string;
  readonly align?: 'center' | 'start';
  /** Optional call-to-action row (e.g. `<Button>`s), rendered under the copy. */
  readonly cta?: ReactNode;
  /**
   * The hero's content width. MUST match the page body's `<Container width>`:
   * the hero used to be hard-wired to `default` while five pages render a
   * `prose` body, so on a left-aligned page the headline's left edge sat ~130px
   * outside everything under it. A shared gutter is the thing that makes a page
   * read as one column rather than two.
   */
  readonly width?: ContainerWidth;
  readonly className?: string;
}

export function PageHero({
  eyebrow,
  title,
  description,
  titleId,
  align = 'center',
  cta,
  className,
  width = 'default',
}: PageHeroProps): ReactElement {
  const isCentered = align === 'center';
  return (
    <header
      className={[
        'relative isolate overflow-hidden border-b border-[color:var(--bd-line)] bg-[color:var(--bd-page)]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* The faint brand wash. `-z-10` under the isolated stacking context so it
          never covers the copy, and anchored to the top edge so the band fades
          back to the page ground before the content below it. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[280px]"
        style={{
          background:
            'radial-gradient(58% 100% at 50% 0%, rgba(43,98,255,.05), rgba(22,210,255,.03) 46%, transparent 72%)',
        }}
      />
      {/* The brand rule on the bottom edge, sitting over the hairline. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-[-1px] h-px"
        style={{ background: 'var(--bd-gradient)', opacity: 0.5 }}
      />
      <Container
        width={width}
        className={`relative py-16 sm:py-[92px] ${isCentered ? 'text-center' : 'text-start'}`}
      >
        {eyebrow !== undefined && eyebrow !== null && eyebrow !== '' ? (
          <p className="mb-5 font-mono rtl:font-sans text-[11px] uppercase tracking-[0.16em] text-[color:var(--bd-subtle)] rtl:normal-case rtl:tracking-normal">
            {eyebrow}
          </p>
        ) : null}
        <h1
          id={titleId}
          className="text-balance text-[clamp(2rem,4.4vw,3.25rem)] font-semibold leading-[1.02] tracking-[-0.042em] text-[color:var(--bd-ink)]"
        >
          {title}
        </h1>
        {description !== undefined && description !== null && description !== '' ? (
          <p
            className={`mt-5 text-pretty text-[1.05rem] leading-[1.62] text-[color:var(--bd-body)] ${
              isCentered ? 'mx-auto max-w-2xl' : 'max-w-2xl'
            }`}
          >
            {description}
          </p>
        ) : null}
        {cta !== undefined && cta !== null && cta !== '' ? (
          <div
            className={`mt-8 flex flex-wrap items-center gap-3 ${
              isCentered ? 'justify-center' : 'justify-start'
            }`}
          >
            {cta}
          </div>
        ) : null}
      </Container>
    </header>
  );
}
